<script setup>
import { ref } from "vue";
import { FormGroupListbox, FormSection, FormTextarea } from "@Components/Form";
import LoadingAnimation from "@Components/Animation/Loading";
import { useForm } from "@/form";
import { PrimaryButton } from "@Components/Button";
import { MagicIcon } from "@Icons/index";
import { useI18n } from "vue-i18n";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import _filter from "lodash/filter";
import _cloneDeep from "lodash/cloneDeep";
import { models as imagesModels, getProvider as getImageProvider } from "@Utils/image-models";

const props = defineProps({
    persona: Object,
});

const processing = ref(false);
const { t: __ } = useI18n();

const regenerateForm = useForm({
    id: props.persona.id,
    model: "flux_1_dev",
    custom_instructions: "",
});

const avatarUrl = ref(props.persona.user_large_avatar_url);
const regenerate = async () => {
    if (regenerateForm.processing) return;

    const response = await regenerateForm.post("personas_update_avatar");

    avatarUrl.value = response?.user_large_avatar_url;
};

const imagesModelsGroups = _map(_groupBy(_filter(_cloneDeep(imagesModels), "is_ai"), "provider"), (items, provider) => {
    return {
        name: getImageProvider(provider).name,
        items: items,
    };
});
</script>

<template>
    <FormSection>
        <template #title> {{ __("Avatar") }} </template>

        <template #form>
            <div class="sm:col-span-full">
                <div class="flex flex-col items-start space-y-6 sm:flex-row sm:space-x-6 sm:space-y-0">
                    <div class="w-48 flex flex-col flex-none">
                        <div class="mx-auto w-48 relative">
                            <img :src="avatarUrl" class="w-48 h-48 object-cover bg-gray-50 rounded-lg" />
                            <div v-if="processing" class="bg-opacity-60 absolute inset-0 flex justify-center items-center bg-white animate-pulse">
                                <LoadingAnimation class="w-16 h-16" />
                            </div>
                        </div>
                    </div>

                    <div class="flex-1">
                        <div
                            class="max-w-[512px] p-6 text-gray-600 bg-gray-50 rounded-lg dark:text-gray-400 dark:bg-gray-900"
                            v-html="
                                __(
                                    'Cette fonctionnalité nécessite le plugin Simple Local Avatars. Il est disponible sur le {startlink}store WordPress{endlink}.',
                                    {
                                        startlink:
                                            '<a href=\'https://wordpress.org/plugins/simple-local-avatars/\' target=\'_blank\' class=\'font-bold text-gray-900 dark:text-white hover:underline\'>',
                                        endlink: '</a>',
                                    }
                                )
                            "></div>
                    </div>
                </div>
                <div class="flex flex-col mt-6 space-y-3">
                    <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Regénérer l'avatar") }}</div>
                    <div class="w-full max-w-sm">
                        <FormGroupListbox v-model="regenerateForm.model" :groups="imagesModelsGroups" required />
                    </div>
                    <FormTextarea
                        :label="__('Instructions personnalisées')"
                        rows="5"
                        v-model="regenerateForm.custom_instructions"
                        :errors="regenerateForm.errors.get('custom_instructions')" />
                    <div>
                        <PrimaryButton @click="regenerate" :processing="regenerateForm.processing" :icon="MagicIcon">
                            {{ __("Regénérer l'avatar") }}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>
